import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import EngineeringIcon from "@mui/icons-material/Engineering";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { format } from "date-fns";
import {
  MaintenanceTicket,
  useGetAdminMaintenanceTicketsQuery,
  useGetPropertiesQuery,
  usePatchAdminMaintenanceTicketsByIdMutation,
} from "../../redux/residentPortalApi";

type TicketStatus = "open" | "in-progress" | "resolved" | "closed";

const STATUSES = ["open", "in-progress", "resolved", "closed"];
const CATEGORIES = [
  "plumbing",
  "electrical",
  "structural",
  "appliance",
  "pest-control",
  "other",
];
const PRIORITIES = ["low", "medium", "high", "urgent"];

const statusConfig: Record<
  string,
  {
    label: string;
    color: "success" | "warning" | "error" | "default" | "info";
    icon: React.ReactElement;
  }
> = {
  open: {
    label: "Open",
    color: "warning",
    icon: <HourglassEmptyIcon fontSize="small" />,
  },
  "in-progress": {
    label: "In Progress",
    color: "info",
    icon: <EngineeringIcon fontSize="small" />,
  },
  resolved: {
    label: "Resolved",
    color: "success",
    icon: <CheckCircleIcon fontSize="small" />,
  },
  closed: {
    label: "Closed",
    color: "default",
    icon: <CancelIcon fontSize="small" />,
  },
};

const priorityConfig: Record<
  string,
  { label: string; color: "error" | "warning" | "info" | "default" }
> = {
  urgent: { label: "Urgent", color: "error" },
  high: { label: "High", color: "warning" },
  medium: { label: "Medium", color: "info" },
  low: { label: "Low", color: "default" },
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const s = statusConfig[status] ?? {
    label: status,
    color: "default" as any,
    icon: <></>,
  };
  return <Chip label={s.label} color={s.color} size="small" icon={s.icon} />;
};

const PriorityChip: React.FC<{ priority: string }> = ({ priority }) => {
  const p = priorityConfig[priority] ?? {
    label: priority,
    color: "default" as any,
  };
  return (
    <Chip label={p.label} color={p.color} size="small" variant="outlined" />
  );
};

const capitalize = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, " ");

const MaintenanceAdmin: React.FC = () => {
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterUnitNumber, setFilterUnitNumber] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] =
    useState<MaintenanceTicket | null>(null);
  const [editStatus, setEditStatus] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const {
    data: tickets,
    isFetching,
    refetch,
  } = useGetAdminMaintenanceTicketsQuery({
    status: filterStatus || undefined,
    category: filterCategory || undefined,
    propertyId: undefined,
  });

  const [patchTicket, { isLoading: isUpdating }] =
    usePatchAdminMaintenanceTicketsByIdMutation();

  const { data: properties } = useGetPropertiesQuery();
  const unitNumberByPropertyId = new Map(
    (properties ?? [])
      .filter((property) => !!property.id)
      .map((property) => [property.id as string, property.unitNumber]),
  );

  const openDialog = (ticket: MaintenanceTicket) => {
    setSelectedTicket(ticket);
    setEditStatus(ticket.status);
    setEditNotes(ticket.adminNotes ?? "");
    setUpdateError(null);
    setUpdateSuccess(false);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedTicket(null);
  };

  const handleUpdate = async () => {
    if (!selectedTicket?.id) return;
    setUpdateError(null);
    try {
      await patchTicket({
        id: selectedTicket.id,
        body: { status: editStatus as TicketStatus, adminNotes: editNotes },
      }).unwrap();
      setUpdateSuccess(true);
      refetch();
      setTimeout(() => closeDialog(), 800);
    } catch (err: any) {
      setUpdateError(
        err?.data?.error?.message ??
          "Failed to update ticket. Please try again.",
      );
    }
  };

  const clearFilters = () => {
    setFilterStatus("");
    setFilterCategory("");
    setFilterUnitNumber("");
  };

  const filteredTickets = (tickets ?? []).filter((t) => {
    if (filterUnitNumber.trim()) {
      const unit = (t.propertyId && unitNumberByPropertyId.get(t.propertyId))?.toString() ?? "";
      if (!unit.includes(filterUnitNumber.trim())) return false;
    }
    return true;
  });

  const hasFilters = !!(filterStatus || filterCategory || filterUnitNumber);

  // Summary stats
  const openCount = filteredTickets.filter((t) => t.status === "open").length;
  const inProgressCount = filteredTickets.filter((t) => t.status === "in-progress").length;
  const resolvedCount = filteredTickets.filter((t) => t.status === "resolved").length;

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#2c3e50",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <BuildIcon sx={{ color: "#3498db" }} />
          Maintenance Tickets
        </Typography>
        <Tooltip title="Refresh">
          <Button
            variant="outlined"
            size="small"
            onClick={() => refetch()}
            startIcon={<RefreshIcon />}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Refresh
          </Button>
        </Tooltip>
      </Stack>

      {/* Summary Chips */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        <Chip
          icon={<HourglassEmptyIcon />}
          label={`Open: ${openCount}`}
          color="warning"
        />
        <Chip
          icon={<EngineeringIcon />}
          label={`In Progress: ${inProgressCount}`}
          color="info"
        />
        <Chip
          icon={<CheckCircleIcon />}
          label={`Resolved: ${resolvedCount}`}
          color="success"
        />
        <Chip label={`Total: ${filteredTickets.length}`} variant="outlined" />
      </Stack>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{ border: "1px solid #e8ecef", borderRadius: "12px", p: 3, mb: 3 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <FilterListIcon sx={{ color: "#7f8c8d" }} />
          <Typography sx={{ fontWeight: 600, color: "#2c3e50" }}>
            Filters
          </Typography>
          {hasFilters && (
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: "none", color: "#e74c3c", ml: 1 }}
            >
              Clear All
            </Button>
          )}
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {capitalize(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {capitalize(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Unit Number"
              value={filterUnitNumber}
              onChange={(e) => setFilterUnitNumber(e.target.value)}
              placeholder="Filter by unit number"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      {isFetching ? (
        <Stack spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} />
          ))}
        </Stack>
      ) : filteredTickets.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e8ecef",
            borderRadius: "12px",
            p: 6,
            textAlign: "center",
          }}
        >
          <BuildIcon sx={{ fontSize: 64, color: "#bdc3c7", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "#7f8c8d" }}>
            No maintenance tickets found
          </Typography>
          {hasFilters && (
            <Typography variant="body2" sx={{ color: "#95a5a6", mt: 1 }}>
              Try adjusting or clearing the filters.
            </Typography>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid #e8ecef",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 700, color: "#2c3e50" }}>
                  Title
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#2c3e50" }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#2c3e50" }}>
                  Priority
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#2c3e50" }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#2c3e50" }}>
                  Unit
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#2c3e50" }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#2c3e50" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.map((ticket: MaintenanceTicket) => (
                <TableRow key={ticket.id} hover>
                  <TableCell>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#2c3e50",
                        fontSize: "0.9rem",
                      }}
                    >
                      {ticket.title}
                    </Typography>
                    <Typography
                      sx={{ color: "#95a5a6", fontSize: "0.78rem", mt: 0.25 }}
                      noWrap
                    >
                      {ticket.description.length > 60
                        ? ticket.description.slice(0, 60) + "…"
                        : ticket.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: "#555", fontSize: "0.875rem" }}>
                      {capitalize(ticket.category)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <PriorityChip priority={ticket.priority} />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={ticket.status} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        color: "#2c3e50",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      }}
                    >
                      {ticket.propertyId && unitNumberByPropertyId.get(ticket.propertyId) != null
                        ? `Unit ${unitNumberByPropertyId.get(ticket.propertyId)}`
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: "#7f8c8d", fontSize: "0.8rem" }}>
                      {ticket.createdAt
                        ? format(new Date(ticket.createdAt), "dd MMM yyyy")
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditNoteIcon />}
                      onClick={() => openDialog(ticket)}
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                      }}
                    >
                      Update
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Update Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#2c3e50", pb: 1 }}>
          Update Ticket
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedTicket && (
            <Stack spacing={3}>
              <Box
                sx={{ backgroundColor: "#f8f9fa", borderRadius: "8px", p: 2 }}
              >
                <Typography sx={{ fontWeight: 600, color: "#2c3e50" }}>
                  {selectedTicket.title}
                </Typography>
                <Typography
                  sx={{ color: "#7f8c8d", fontSize: "0.875rem", mt: 0.5 }}
                >
                  {selectedTicket.description}
                </Typography>
                {selectedTicket.residentNotes && (
                  <Box
                    sx={{
                      backgroundColor: "#f0fdf4",
                      borderRadius: "6px",
                      p: 1.5,
                      mt: 1.5,
                      border: "1px solid #86efac",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#16a34a",
                        fontSize: "0.8rem",
                        mb: 0.5,
                      }}
                    >
                      Resident Update:
                    </Typography>
                    <Typography sx={{ color: "#2c3e50", fontSize: "0.875rem" }}>
                      {selectedTicket.residentNotes}
                    </Typography>
                  </Box>
                )}
              </Box>

              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  label="Status"
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {capitalize(s)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Admin Notes (visible to resident)"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                placeholder="Add a note for the resident about this issue..."
              />

              {updateError && (
                <Alert severity="error" sx={{ borderRadius: "8px" }}>
                  {updateError}
                </Alert>
              )}
              {updateSuccess && (
                <Alert severity="success" sx={{ borderRadius: "8px" }}>
                  Ticket updated successfully.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={closeDialog}
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={isUpdating}
            startIcon={
              isUpdating ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              backgroundColor: "#3498db",
              "&:hover": { backgroundColor: "#2980b9" },
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceAdmin;
